import React, {useEffect, useMemo, useState} from "react";
import {
    Card,
    Col,
    DatePicker,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import type {ColumnsType} from "antd/es/table";
import dayjs from "dayjs";
import api from "../../api";

const {Title, Text} = Typography;

type Agent = {
    id: number;
    name: string;
    program: string;
};

type TripRow = {
    id: number;
    customer: string;
    checkin_date: string;
    checkout_date: string;
    buyin_amount: string | number | null;
    rebuys: string | number | null;
    final_balance: string | number | null;
    win_loss: string | number | null;
    junket_share: string | number | null;
    bonus_amount: string | number | null;
    expenses: string | number | null;
    ebaf: string | number | null;
    pnl: string | number | null;
};

type MonthlySettlement = {
    month: number;
    monthly_win_loss: string | number;
    opening_carryover: string | number;
    adjusted_win_loss: string | number;
    commissionable_win_loss: string | number;
    closing_carryover: string | number;
    monthly_junket_share: string | number;
    monthly_bonus_amount: string | number;
    monthly_expenses: string | number;
    ebaf: string | number;
    agent_fee_rate: string | number;
    agent_fees: string | number;
    pnl: string | number;
    trips: TripRow[];
};

type SettlementResponse = {
    agents: Agent[];
    selected_agent: Agent | null;
    year: number;
    months: MonthlySettlement[];
};

const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const toNumber = (value: string | number | null | undefined): number => {
    if (value === null || value === undefined || value === "") return 0;
    return Number(value);
};

const formatKRW = (value: string | number | null | undefined): string => {
    const num = toNumber(value);
    return num.toLocaleString("en-US", {
        maximumFractionDigits: 0,
    });
};

const formatSignedKRW = (value: string | number | null | undefined): React.ReactNode => {
    const num = toNumber(value);

    if (num > 0) {
        return <Text type="success">{formatKRW(num)}</Text>;
    }

    if (num < 0) {
        return <Text type="danger">{formatKRW(num)}</Text>;
    }

    return <Text>{formatKRW(num)}</Text>;
};

const formatPercent = (value: string | number | null | undefined): string => {
    const num = toNumber(value);
    return `${(num * 100).toFixed(2)}%`;
};

const AgentSettlementsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [selectedAgentId, setSelectedAgentId] = useState<number | undefined>();
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const [year, setYear] = useState<number>(dayjs().year());
    const [months, setMonths] = useState<MonthlySettlement[]>([]);

    const fetchSettlements = async (agentId?: number, selectedYear?: number) => {
        setLoading(true);

        try {
            const params: Record<string, number> = {
                year: selectedYear || year,
            };

            if (agentId) {
                params.agent_id = agentId;
            }

            const response = await api.get<SettlementResponse>("/daia/agent-settlements/", {
                params,
            });

            setAgents(response.data.agents || []);
            setSelectedAgent(response.data.selected_agent);
            setMonths(response.data.months || []);
            setYear(response.data.year);

            if (response.data.selected_agent) {
                setSelectedAgentId(response.data.selected_agent.id);
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to load agent settlements.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettlements();
    }, []);

    const handleAgentChange = (agentId: number) => {
        setSelectedAgentId(agentId);
        fetchSettlements(agentId, year);
    };

    const handleYearChange = (date: dayjs.Dayjs | null) => {
        if (!date) return;

        const selectedYear = date.year();
        setYear(selectedYear);
        fetchSettlements(selectedAgentId, selectedYear);
    };

    const summary = useMemo(() => {
        const totals = months.reduce(
            (acc, row) => {
                acc.rawWinLoss += toNumber(row.monthly_win_loss);
                acc.ebaf += toNumber(row.ebaf);
                acc.agentFees += toNumber(row.agent_fees);
                return acc;
            },
            {
                rawWinLoss: 0,
                ebaf: 0,
                agentFees: 0,
                netAfterAgentFees: 0,
                endingCarryover: 0,
            }
        );

        totals.netAfterAgentFees = totals.ebaf - totals.agentFees;

        const lastMonth = months[months.length - 1];
        totals.endingCarryover = lastMonth
            ? toNumber(lastMonth.closing_carryover)
            : 0;

        return totals;
    }, [months]);

    const tripColumns: ColumnsType<TripRow> = [
        {
            title: "Customer",
            dataIndex: "customer",
            key: "customer",
        },
        {
            title: "Check-in",
            dataIndex: "checkin_date",
            key: "checkin_date",
        },
        {
            title: "Check-out",
            dataIndex: "checkout_date",
            key: "checkout_date",
        },
        {
            title: "Buy-in",
            dataIndex: "buyin_amount",
            key: "buyin_amount",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Rebuys",
            dataIndex: "rebuys",
            key: "rebuys",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Final Balance",
            dataIndex: "final_balance",
            key: "final_balance",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Win/Loss",
            dataIndex: "win_loss",
            key: "win_loss",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Bonus",
            dataIndex: "bonus_amount",
            key: "bonus_amount",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Expenses",
            dataIndex: "expenses",
            key: "expenses",
            align: "right",
            render: formatKRW,
        },
        {
            title: "EBAF",
            dataIndex: "ebaf",
            key: "ebaf",
            align: "right",
            render: formatSignedKRW,
        },
    ];

    const columns: ColumnsType<MonthlySettlement> = [
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            fixed: "left",
            render: (month: number) => monthNames[month],
        },
        {
            title: "Raw W/L",
            dataIndex: "monthly_win_loss",
            key: "monthly_win_loss",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Opening Carryover",
            dataIndex: "opening_carryover",
            key: "opening_carryover",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Adjusted W/L",
            dataIndex: "adjusted_win_loss",
            key: "adjusted_win_loss",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Commissionable W/L",
            dataIndex: "commissionable_win_loss",
            key: "commissionable_win_loss",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Bonus",
            dataIndex: "monthly_bonus_amount",
            key: "monthly_bonus_amount",
            align: "right",
            render: formatKRW,
        },
        {
            title: "Expenses",
            dataIndex: "monthly_expenses",
            key: "monthly_expenses",
            align: "right",
            render: formatKRW,
        },
        {
            title: "EBAF",
            dataIndex: "ebaf",
            key: "ebaf",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Rate",
            dataIndex: "agent_fee_rate",
            key: "agent_fee_rate",
            align: "right",
            render: formatPercent,
        },
        {
            title: "Agent Fee",
            dataIndex: "agent_fees",
            key: "agent_fees",
            align: "right",
            render: formatKRW,
        },
        {
            title: "P&L",
            dataIndex: "pnl",
            key: "pnl",
            align: "right",
            render: formatSignedKRW,
        },
        {
            title: "Closing Carryover",
            dataIndex: "closing_carryover",
            key: "closing_carryover",
            align: "right",
            render: formatSignedKRW,
        },
    ];

    return (
        <div style={{padding: 24}}>
            <Space direction="vertical" size="large" style={{width: "100%"}}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Title level={3} style={{marginBottom: 0}}>
                            Agent Settlements
                        </Title>
                        <Text type="secondary">
                            Daia Premium Rooms
                        </Text>
                    </Col>

                    <Col>
                        <Space>
                            <Select
                                style={{width: 260}}
                                placeholder="Select agent"
                                value={selectedAgentId}
                                onChange={handleAgentChange}
                                options={agents.map((agent) => ({
                                    value: agent.id,
                                    label: `${agent.name} ${agent.program ? `(${agent.program})` : ""}`,
                                }))}
                            />

                            <DatePicker
                                picker="year"
                                value={dayjs(`${year}-01-01`)}
                                onChange={handleYearChange}
                                allowClear={false}
                            />
                        </Space>
                    </Col>
                </Row>

                {selectedAgent && (
                    <Card>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Agent</Text>
                                <br/>
                                <Text strong>{selectedAgent.name}</Text>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Program</Text>
                                <br/>
                                <Tag color="blue">{selectedAgent.program}</Tag>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Profit Before Agent Fees</Text>
                                <br/>
                                {formatSignedKRW(summary.ebaf)}
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Agent Fees</Text>
                                <br/>
                                <Text strong>{formatKRW(summary.agentFees)}</Text>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Net After Agent Fees</Text>
                                <br/>
                                {formatSignedKRW(summary.netAfterAgentFees)}
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                                <Text type="secondary">Ending Carryover</Text>
                                <br/>
                                {formatSignedKRW(summary.endingCarryover)}
                            </Col>
                        </Row>
                    </Card>
                )}

                <Spin spinning={loading}>
                    <Table
                        rowKey="month"
                        columns={columns}
                        dataSource={months}
                        bordered
                        scroll={{x: 1600}}
                        pagination={false}
                        expandable={{
                            expandedRowRender: (record) => (
                                <Table
                                    rowKey="id"
                                    columns={tripColumns}
                                    dataSource={record.trips}
                                    pagination={false}
                                    size="small"
                                    bordered
                                />
                            ),
                            rowExpandable: (record) => record.trips && record.trips.length > 0,
                        }}
                    />
                </Spin>
            </Space>
        </div>
    );
};

export default AgentSettlementsPage;